import { FC } from "react";
// i18next
import { useTranslation } from "react-i18next";
// Icons
import { ChevronRight } from 'lucide-react';
// Types
import QuizTypes from "types/QuizTypes";

export type QuizTypeOption = {
  id: QuizTypes;
  title: string;
  description: string;
  icon: FC<{ className?: string }>;
  status: string;
};

type Props = {
  quizTypes: QuizTypeOption[];
  selectedType: QuizTypes;
  setSelectedType: (type: QuizTypes) => void;
  onNext: () => void;
}

const QuizTypeStep:FC<Props> = ({ quizTypes, selectedType, setSelectedType, onNext }) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {quizTypes.map((type) => (
          <button
            key={type.id}
            disabled={type.status === 'pending'}
            onClick={() => setSelectedType(type.id)}
            className={`text-left p-5 rounded-xl border transition-all duration-200 relative overflow-hidden flex flex-col h-full
              ${type.status === 'pending'
                ? 'border-gray-700/50 bg-[#2a2a2e]/50 opacity-60 cursor-not-allowed'
                : selectedType === type.id
                  ? 'border-orange-500 bg-orange-500/10 shadow-[0_0_15px_rgba(249,115,22,0.1)]'
                  : 'border-gray-700 bg-[#2a2a2e] hover:border-gray-500 hover:bg-[#303035]'
              }
            `}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <type.icon className={`w-5 h-5 ${selectedType === type.id && type.status !== 'pending' ? 'text-orange-500' : 'text-gray-400'}`} />
                <h3 className="font-semibold text-gray-100">{type.title}</h3>
              </div>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed flex-grow">{type.description}</p>

            {type.status === 'pending' && (
              <span className="absolute top-4 right-4 text-xs uppercase tracking-wider font-bold text-gray-500 bg-gray-800 px-2 py-1 rounded">
                {t('dialogs.quizAgent.comingSoon')}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="flex justify-end">
        <button
          onClick={onNext}
          disabled={!selectedType}
          className={`
            flex items-center gap-2 px-8 py-3 rounded-xl font-medium text-white transition-all
            ${!selectedType
              ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
              : 'bg-orange-600 hover:bg-orange-500 hover:shadow-lg hover:-translate-y-0.5'
            }
          `}
        >
          <span className="flex items-center gap-2">
            {t('buttons.next')}
            <ChevronRight className="w-5 h-5" />
          </span>
        </button>
      </div>
    </div>
  )
};

export default QuizTypeStep;
